import { toRaw } from 'vue'

/**
 * Flattens the keys of an object
 * @example
 * const obj = { a: { b: { c: 'd' } } }
 * const flat = flattenKeys(obj) // { 'a.b.c': 'd' }
 * @param {object} obj
 * @param {string} [prefix=null] - The prefix to use for the keys
 * @returns {object}
 */
export function flattenKeys(obj, prefix = null) {
  const prefixKey = prefix ? `${prefix}.` : ''
  return Object.keys(obj).reduce((acc, key) => {
    const newKey = `${prefixKey}${key}`
    const value = obj[key]
    if (typeof value === 'object') {
      return { ...acc, ...flattenKeys(value, newKey) }
    }
    return { ...acc, [newKey]: value }
  }, {})
}

/**
 * Recursively converts any Vue proxy values to their raw values.
 * Only recurses into plain objects and arrays.
 */
export function toRawDeep(value, seen = new WeakMap()) {
  if (value === null || typeof value !== 'object') {
    return value
  }
  // Handle potential circular references
  if (seen.has(value)) {
    return seen.get(value)
  }
  
  const isObject = Object.prototype.toString.call(value) === '[object Object]'
  const isArray = Array.isArray(value)
  const raw = toRaw(value)
  const rawValue = isObject ? {} : isArray ? [] : raw
  seen.set(value, rawValue)

  if (isObject || isArray) {
    for (const key in raw) {
      rawValue[key] = toRawDeep(raw[key], seen)
    }
  }
  return rawValue
}

export function cloneValue(value) {
  if ('structuredClone' in window) {
    const raw = toRawDeep(value)
    return structuredClone(raw)
  } else {
    return JSON.parse(JSON.stringify(value))
  }
}
